import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../../store/app.store';
import useTema from '../../hooks/useTema';
import { IcSearch, IcX, IcBuilding, IcFileText, IcUser, IcCalendar, IcLayers } from './Icons';

const TIPO_LABEL = {
  cliente: { label: 'Cliente', icon: <IcBuilding size={13} />, cor: '#6d28d9', rota: '/clientes' },
  chamado: { label: 'Chamado', icon: <IcFileText size={13} />, cor: '#0ea5e9', rota: '/chamados' },
  producao: { label: 'Produção', icon: <IcLayers size={13} />, cor: '#f59e0b', rota: '/midias' },
  reuniao:  { label: 'Reunião', icon: <IcCalendar size={13} />, cor: '#10b981', rota: '/reunioes' },
  lead:     { label: 'Lead CRM', icon: <IcUser size={13} />, cor: '#ef4444', rota: '/crm' },
};

export default function SearchModal({ onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const t = useTema();

  const clientes  = useAppStore(s => s.clientes);
  const chamados  = useAppStore(s => s.chamados);
  const producoes = useAppStore(s => s.producoes);
  const reunioes  = useAppStore(s => s.reunioes || []);
  const leads     = useAppStore(s => s.leads);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  const q = query.toLowerCase().trim();

  const resultados = q.length < 2 ? [] : [
    ...clientes.map(c => ({ tipo: 'cliente', id: c.id, titulo: c.razao_social, sub: c.email || c.cidade || '' })),
    ...chamados.map(c => ({ tipo: 'chamado', id: c.id, titulo: c.titulo, sub: c.status })),
    ...producoes.map(p => ({ tipo: 'producao', id: p.id, titulo: p.titulo, sub: p.status })),
    ...reunioes.map(r => ({ tipo: 'reuniao', id: r.id, titulo: r.titulo, sub: r.data || '' })),
    ...leads.map(l => ({ tipo: 'lead', id: l.id, titulo: l.empresa || l.contato, sub: l.etapa || '' })),
  ].filter(r => r.titulo?.toLowerCase().includes(q) || r.sub?.toLowerCase().includes(q)).slice(0, 10);

  const ir = (tipo) => { navigate(TIPO_LABEL[tipo].rota); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <div className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>

        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
          <IcSearch size={16} style={{ color: t.textMuted, flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar clientes, chamados, produções..."
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: t.text }}
            onKeyDown={e => e.key === 'Escape' && onClose()}
          />
          {query && (
            <button onClick={() => setQuery('')} style={{ color: t.textMuted }}>
              <IcX size={14} />
            </button>
          )}
        </div>

        {/* Resultados */}
        {q.length >= 2 && (
          <div className="max-h-80 overflow-y-auto">
            {resultados.length === 0 ? (
              <p className="text-sm text-center py-8" style={{ color: t.textMuted }}>Nenhum resultado encontrado</p>
            ) : resultados.map((r, i) => {
              const meta = TIPO_LABEL[r.tipo];
              return (
                <button key={i} onClick={() => ir(r.tipo)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                  style={{ borderBottom: `1px solid ${t.borderLight}` }}
                  onMouseEnter={e => e.currentTarget.style.background = t.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ background: meta.cor + '20', color: meta.cor }}>
                    {meta.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: t.text }}>{r.titulo}</p>
                    <p className="text-xs truncate capitalize" style={{ color: t.textMuted }}>{meta.label}{r.sub ? ` · ${r.sub}` : ''}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {q.length < 2 && (
          <div className="px-4 py-6 text-center">
            <p className="text-xs" style={{ color: t.textMuted }}>Digite ao menos 2 caracteres para buscar</p>
          </div>
        )}

        <div className="px-4 py-2 flex justify-end" style={{ borderTop: `1px solid ${t.border}` }}>
          <span className="text-[10px]" style={{ color: t.textMuted }}>ESC para fechar</span>
        </div>
      </div>
    </div>
  );
}
